import { Context } from '@nuxt/types';
import { MutationTree, ActionTree } from 'vuex';
import rison from 'rison';
import { TocInteractive, Viewer } from '@/types/Viewer';
import { DocumentConfig } from '@/types/document-config';
import { DocumentHits } from '@/utils/lookupUtils';
import { Nullable } from '@/types/nullable';
import { PartialSearchQuery } from '@/types/SearchQuery';
import { BookSnippet, DocRecord, DocumentTypeEnum } from 'wklr-backend-sdk/models';
import { generateNewToc, TocNodeExtended } from '@/utils/tocUtils';
import {
  LabsWandhStreamParams,
  NonNullableLabsWandhQuestionHistoryListItem,
  isNonNullableLabsWandhQuestionHistoryListItem,
} from 'wklr-backend-sdk/repos/labs';
import { LabsWandhModelSelectionEnum } from 'wklr-backend-sdk/models';
import { useAuth } from '@/plugins/auth';
import { useRepositories } from '@/plugins/repositories';

export interface FindState {
  /** 現在のハイライトキーワード */
  keywords: string[];
  hit: HitState;
}

export interface HitState {
  /** 現在のヒット箇所 (0-index) */
  current: number | null;
  /** 現在のヒット位置が属するコンテントの series */
  currentSeries: number | null;
  /** ヒット情報 */
  documentHits: DocumentHits | null;
  /** 巻毎のトータルヒット数 */
  totalHitsByVolume: Record<string, number>;
}

export interface PersistentState {
  /** 検索結果画面１ページあたりの表示件数 */
  perPage: number | null;

  /** 閲覧画面のユーザー設定 */
  documentConfig: DocumentConfig;

  /**
   * PDF見開き notice を表示したことがあるか
   * @note PDF 見開き設定の全 PDF 共通化に伴い、これまで notice を表示したことのある人にも notice を表示する必要があるため hasSeenPdfSpreadModeNotice2 としている
   */
  hasSeenPdfSpreadModeNotice2: boolean;

  /** W&H D1トライアル導線を閉じたか */
  hasClosedWandhD1Trial: boolean;

  /** Holmesで参照をタイトル名+セクション番号で表示するか */
  showDetailedReference: boolean;

  /** ニュースパネルを表示するか（legalscape組織のみ） */
  showNewsPanel: boolean;

  /** 検索クエリの永続データ */
  search: {
    keyword: string;
    bookQuery: PartialSearchQuery;
    guidelineQuery: PartialSearchQuery;
    pubcomQuery: PartialSearchQuery;
    lawQuery: PartialSearchQuery;
  };

  /** Labs関連の状態 */
  labs: LabsState;
}

interface LabsWandhQuotaByModel {
  remainingQuota: number | null;
}

interface LabsWandhQuota {
  /**
   * @deprecated 代わりに remainingQuotaByModel を使う
   */
  remainingQuota: number;
  remainingQuotaByModel: {
    standard: LabsWandhQuotaByModel;
    advanced: LabsWandhQuotaByModel;
    experimental: LabsWandhQuotaByModel[];
  };
}

export type InputQuery = {
  model: string | null;
  selection: LabsWandhModelSelectionEnum;
  question: string;
  filter: {
    /** 書籍指定検索におけるIDリスト（null ⇔ (iff) 全選択と見なす） */
    id: Record<'store' | 'other', string[] | null>;
    publishedOnOrAfterYear?: number | string;
    genre: string[];
    documentType: Extract<DocumentTypeEnum, 'case' | 'book' | 'guideline' | 'law'>[];
    /** 権限がないため利用できない文献種別 */
    unavailableDocumentTypes?: Extract<DocumentTypeEnum, 'case' | 'book' | 'guideline' | 'law'>[];
  };
};

export interface LabsState {
  wandh: {
    quota: LabsWandhQuota | null;
    streamParams: LabsWandhStreamParams | null;
    history: NonNullableLabsWandhQuestionHistoryListItem[];
    isInputQueryInitialized: boolean;
    inputQuery: InputQuery;
  };
}

export interface State {
  global: {
    shareUrlDialogDocument: Nullable<DocRecord | BookSnippet>;
    routeChanging: boolean;
    firstAccessOfTheDay: boolean;
    loading: boolean;
    hasClosedAccountConfirmationDialog: boolean;
  };
  search: {
    query: PartialSearchQuery;
    orderId: number;
    suggestionItems: string[];
    preview: string | false;
    selectedSnippet: string | null;
  };
  searchBox: {
    /** 検索ボックスに入力された値を保持する Store。 コミットの際に state.search.query.keyword に適用される */
    keyword: string;
    /** 詳細検索を表示するかどうか */
    showAdvanceSearch: boolean;
  };
  document: {
    activeViewer: Viewer | null;
    find: FindState;
    toc: Record<string, TocInteractive>;
  };
  persistent: PersistentState;
}

export const state = (): State => ({
  global: {
    shareUrlDialogDocument: null,
    routeChanging: false,
    firstAccessOfTheDay: true,
    loading: false,
    hasClosedAccountConfirmationDialog: false,
  },
  search: {
    query: {},
    orderId: 0,
    suggestionItems: [],
    preview: false,
    selectedSnippet: null,
  },
  searchBox: {
    keyword: '',
    showAdvanceSearch: false,
  },
  document: {
    activeViewer: null,
    find: {
      keywords: [],
      hit: {
        current: null,
        currentSeries: null,
        documentHits: null,
        totalHitsByVolume: {},
      },
    },
    toc: {},
  },
  persistent: {
    perPage: null,
    documentConfig: {
      bodyFontSize: 18,
      maxWidth: 'default',
    },
    hasSeenPdfSpreadModeNotice2: false,
    hasClosedWandhD1Trial: false,
    showDetailedReference: true,
    showNewsPanel: true,
    search: {
      keyword: '',
      bookQuery: {
        type: [DocumentTypeEnum.Book],
      },
      guidelineQuery: {
        type: [DocumentTypeEnum.Guideline],
      },
      pubcomQuery: {
        type: [DocumentTypeEnum.Pubcom],
      },
      lawQuery: {
        type: [DocumentTypeEnum.Law],
      },
    },
    labs: {
      wandh: {
        quota: null,
        streamParams: null,
        history: [],
        isInputQueryInitialized: false,
        inputQuery: {
          model: null,
          selection: LabsWandhModelSelectionEnum.Standard,
          question: '',
          filter: {
            id: { store: null, other: null },
            genre: [],
            documentType: ['book', 'guideline', 'law'],
            unavailableDocumentTypes: ['case'],
          },
        },
      },
    },
  },
});

export const getters = {};

export const mutations: MutationTree<State> = {
  startRouteChange(state) {
    state.global.routeChanging = true;
  },
  endRouteChange(state) {
    state.global.routeChanging = false;
  },
  setShareUrlDialogDocument(state: State, documentInfo: DocRecord | BookSnippet) {
    state.global.shareUrlDialogDocument = documentInfo;
  },
  clearShareUrlDialogDocument(state: State) {
    state.global.shareUrlDialogDocument = null;
  },
  setSearchKeyword(state: State, keyword: string) {
    state.searchBox.keyword = keyword;
    state.persistent.search.keyword = keyword;
  },
  showAdvancedSearch(state: State) {
    state.searchBox.showAdvanceSearch = true;
  },
  hideAdvancedSearch(state: State) {
    state.searchBox.showAdvanceSearch = false;
  },
  setSearchOrderId(state: State, orderId: number) {
    state.search.orderId = orderId;
  },
  updateSearchQuery(state: State, query: PartialSearchQuery) {
    state.search.query = query;
    state.searchBox.keyword = query.keyword || '';
  },
  setSuggestionItems(state: State, suggestionItems: string[]) {
    state.search.suggestionItems = suggestionItems;
  },
  setPreview(state: State, preview: string | false) {
    state.search.preview = preview;
  },
  setSelectedSnippet(state: State, selected: string | null) {
    state.search.selectedSnippet = selected;
  },
  setActiveViewer(state: State, viewer: Viewer) {
    state.document.activeViewer = viewer;
  },
  setTocList(
    state: State,
    { toc: tocOriginal, openByDefault }: { toc: Record<string, TocNodeExtended>; openByDefault: 'root' | 'all' },
  ) {
    state.document.toc = generateNewToc(tocOriginal, (toc) => ({
      ...toc,
      open: openByDefault === 'all' || toc.isRoot || null,
      sectionHit: null,
      active: false,
    }));
  },
  updateActiveToc(state: State, payload: { docId: string; activeKeys: number[] }) {
    const { docId, activeKeys } = payload;
    const toc = generateNewToc(state.document.toc, (toc) => ({ ...toc, active: activeKeys.includes(toc.key) }), docId);
    state.document.toc = { ...state.document.toc, ...toc };
  },
  updateSectionHits(state: State, { allVolumesHits }: { allVolumesHits: { [key: string]: DocumentHits } }) {
    const toc = generateNewToc(state.document.toc, (toc) => ({
      ...toc,
      sectionHit: allVolumesHits[toc.docId]?.sectionHits[toc.key] || null,
    }));
    state.document.toc = toc;
  },
  clearSectionHits(state: State, payload: { docId: string }) {
    const { docId } = payload;
    const toc = generateNewToc(state.document.toc, (toc) => ({ ...toc, sectionHit: null }), docId);
    state.document.toc = { ...state.document.toc, ...toc };
  },
  toggleTocOpen(state: State, payload: { key: string; isOpen: boolean }) {
    const { key, isOpen } = payload;
    state.document.toc[key].open = isOpen;
  },
  clearFind(state: State) {
    state.document.find = {
      keywords: [],
      hit: {
        current: null,
        currentSeries: null,
        documentHits: null,
        totalHitsByVolume: {},
      },
    };
  },
  requestUpdateHighlights(state: State, keywords: string[]) {
    state.document.find = {
      keywords,
      hit: {
        current: null,
        currentSeries: null,
        documentHits: null,
        totalHitsByVolume: {},
      },
    };
  },
  setHit(
    state: State,
    { documentHits, totalHitsByVolume }: { documentHits: DocumentHits; totalHitsByVolume: Record<string, number> },
  ) {
    state.document.find.hit = {
      current: null,
      currentSeries: null,
      documentHits,
      totalHitsByVolume,
    };
  },
  setCurrentHit(state: State, payload: { current: number | null; currentSeries: number | null }) {
    state.document.find.hit.current = payload.current;
    state.document.find.hit.currentSeries = payload.currentSeries;
  },
  setPerPage(state: State, perPage: number | null) {
    state.persistent.perPage = perPage;
  },
  setBodyFontSize(state: State, bodyFontSize: DocumentConfig['bodyFontSize']) {
    state.persistent.documentConfig.bodyFontSize = bodyFontSize;
  },
  setMaxWidth(state: State, maxWidth: DocumentConfig['maxWidth']) {
    state.persistent.documentConfig.maxWidth = maxWidth;
  },
  setHasSeenPdfSpreadModeNotice(state: State, hasSeenPdfSpreadModeNotice: boolean) {
    state.persistent.hasSeenPdfSpreadModeNotice2 = hasSeenPdfSpreadModeNotice;
  },
  setHasClosedWandhD1Trial(state: State, closed: boolean) {
    state.persistent.hasClosedWandhD1Trial = closed;
  },
  setShowNewsPanel(state: State, showNewsPanel: boolean) {
    state.persistent.showNewsPanel = showNewsPanel;
  },
  setShowDetailedReference(state: State, showDetailedReference: boolean) {
    state.persistent.showDetailedReference = showDetailedReference;
  },
  setFirstAccessOfTheDay(state: State, flag: boolean) {
    state.global.firstAccessOfTheDay = flag;
  },
  setLoading(state: State, value: boolean) {
    state.global.loading = value;
  },
  setHasClosedAccountConfirmationDialog(state: State, value: boolean) {
    state.global.hasClosedAccountConfirmationDialog = value;
  },
  updateLabsWandhQuota(state: State, quota: LabsWandhQuota) {
    state.persistent.labs.wandh.quota = quota;
  },
  setLabsWandhStreamParams(state: State, params: LabsWandhStreamParams) {
    state.persistent.labs.wandh.streamParams = params;
  },
  resetLabsWandhStreamParams(state: State) {
    state.persistent.labs.wandh.streamParams = null;
  },
  setWandhHistory(state: State, history: NonNullableLabsWandhQuestionHistoryListItem[]) {
    state.persistent.labs.wandh.history = history;
  },
  setLabsWandhInputQueryInitialized(state: State, initialized: boolean) {
    state.persistent.labs.wandh.isInputQueryInitialized = initialized;
  },
  setLabsWandhInputQuery(state: State, inputQuery: InputQuery) {
    state.persistent.labs.wandh.inputQuery = inputQuery;
  },
  updatePersistentBookQuery(state: State, query: PartialSearchQuery) {
    state.persistent.search.bookQuery = query;
  },
  updatePersistentGuidelineQuery(state: State, query: PartialSearchQuery) {
    state.persistent.search.guidelineQuery = query;
  },
  updatePersistentPubcomQuery(state: State, query: PartialSearchQuery) {
    state.persistent.search.pubcomQuery = query;
  },
  updatePersistentLawQuery(state: State, query: PartialSearchQuery) {
    state.persistent.search.lawQuery = query;
  },
};

export const actions: ActionTree<State, unknown> = {
  /** SSR時に事前にフォームを埋めておくのに必要 */
  nuxtServerInit({ commit }, { route }: Context) {
    if (route.query.q) {
      try {
        commit('updateSearchQuery', rison.decode_object<PartialSearchQuery>(<string>route.query.q));
      } catch (_) {
        /* nop */
      }
    }
  },
  resetWandhHistory({ commit }) {
    commit('setWandhHistory', []);
  },
  initializeWandhInputQuery({ commit, state }) {
    if (state.persistent.labs.wandh.isInputQueryInitialized) {
      return;
    }

    const auth = useAuth();
    const documentType = [...state.persistent.labs.wandh.inputQuery.filter.documentType];
    let unavailableTypes = state.persistent.labs.wandh.inputQuery.filter.unavailableDocumentTypes;
    const hasD1Option = auth.permissions.labsQAD1 || auth.user.labsWandhD1Option;

    if (unavailableTypes === undefined) {
      if (!hasD1Option && !documentType.includes('case')) {
        unavailableTypes = ['case'];
      } else {
        // initialize to empty array
        unavailableTypes = [];
      }
    } else {
      unavailableTypes = unavailableTypes || [];
    }

    if (hasD1Option) {
      if (unavailableTypes.includes('case') && !documentType.includes('case')) {
        documentType.push('case');
      }
    }

    const isMaxSearchBoostUnlimited = auth.permissions.labsQA && auth.user.labsWandhPlan?.maxSearchBoost === null;
    const labsWandhModelSelection = isMaxSearchBoostUnlimited
      ? LabsWandhModelSelectionEnum.Advanced
      : LabsWandhModelSelectionEnum.Standard;

    const newUnavailableTypes = hasD1Option
      ? unavailableTypes.filter((type) => type !== 'case')
      : unavailableTypes.includes('case')
        ? unavailableTypes
        : [...unavailableTypes, 'case'];

    commit('setLabsWandhInputQuery', {
      ...state.persistent.labs.wandh.inputQuery,
      question: '',
      selection: labsWandhModelSelection,
      filter: {
        ...state.persistent.labs.wandh.inputQuery.filter,
        documentType,
        unavailableDocumentTypes: newUnavailableTypes,
        publishedOnOrAfterYear: undefined,
      },
    });
    commit('setLabsWandhInputQueryInitialized', true);
  },
  async loadWandhHistory({ commit, state }, params: { after?: string; pagingSize: number }) {
    const repositories = useRepositories();
    const { data: newItems } = await repositories.labs.listLabsWandhQuestionHistories(params.pagingSize, params.after);
    if (newItems.length === 0) {
      return;
    }
    // 不完全なものを除外
    const filtered = newItems.filter(isNonNullableLabsWandhQuestionHistoryListItem);
    commit('setWandhHistory', [...state.persistent.labs.wandh.history, ...filtered]);
  },
  async loadLatestWandhHistory({ commit, state }) {
    const repositories = useRepositories();
    const { data: newItems } = await repositories.labs.listLabsWandhQuestionHistories(1);
    if (newItems.length === 0) {
      return;
    }

    const newItem = newItems[0];
    // 不完全な場合は追加しない
    if (!isNonNullableLabsWandhQuestionHistoryListItem(newItem)) {
      return;
    }
    // すでに追加済みの場合は追加しない
    if (state.persistent.labs.wandh.history.map(({ id }) => id).includes(newItem.id)) {
      return;
    }

    commit('setWandhHistory', [newItem, ...state.persistent.labs.wandh.history]);
  },
  startLoading({ commit }) {
    console.log('startLoading');
    commit('setLoading', true);
  },
  endLoading({ commit }) {
    console.log('endLoading');
    commit('setLoading', false);
  },
};
